import React, { useState, useEffect, useCallback } from 'react';
import { Container, Row, Col, Spinner, Alert, Button } from 'react-bootstrap';
import { Link, useNavigate } from 'react-router-dom';

import { useAuth } from '@/shared/contexts/AuthContext';
import { useAuthenticatedAxios } from '@/shared/services/axiosInstance';
import {
    getCommunities,
    createCommunity,
} from '@/shared/services/communityApi';
import SEO from '@/components/common/SEO';

import CommunitySearch from './components/CommunitySearch';
import CommunityCard from './components/CommunityCard';
import CreateCommunityModal from './components/CreateCommunityModal';

import './Communities.css';

export default function Communities() {
    const { user } = useAuth();
    const axiosInstance = useAuthenticatedAxios();
    const navigate = useNavigate();
    const { isAdmin } = useAuth();

    const [communities, setCommunities] = useState([]);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);
    const [showCreateModal, setShowCreateModal] = useState(false);

    useEffect(() => {
        const fetchCommunities = async () => {
            try {
                setLoading(true);
                const data = await getCommunities();
                setCommunities(data);
                setError(null);
            } catch (err) {
                console.error('Error fetching communities:', err);
                setError('Unable to load communities. Please try again later.');
            } finally {
                setLoading(false);
            }
        };

        fetchCommunities();
    }, []);

    const handleCreateCommunity = async (communityData) => {
        const newCommunity = await createCommunity(
            axiosInstance,
            communityData
        );
        // Site admins create active communities directly — append and navigate.
        // Non-admin requests start as 'pending' and aren't visible publicly,
        // so we leave the list alone and let the modal show its confirmation.
        if (isAdmin) {
            setCommunities((prev) => [newCommunity, ...prev]);
            navigate(`/communities/${newCommunity.slug}`);
        }
    };

    const handleSearch = useCallback(async (search) => {
        try {
            setLoading(true);

            const trimmed = search.trim();
            const query = trimmed === '' ? undefined : trimmed;

            const data = await getCommunities({ search: query });
            setCommunities(data);
            setError(null);
        } catch (err) {
            console.error('Error searching communities:', err);
            setError('Unable to search communities.');
        } finally {
            setLoading(false);
        }
    }, []);

    return (
        <section id="communities" className="page-container">
            <SEO title="Communities" />
            <Container className="py-5">
                {/* Header */}
                <Row className="mb-5">
                    <Col lg={10} className="mx-auto text-center">
                        <h1 className="text-primary mb-4">
                            Communities of Interest
                        </h1>
                        <p className="lead communities-description mb-3">
                            Connect with like-minded individuals passionate
                            about sustainability! Join communities organized
                            around topics, locations, or initiatives that matter
                            to you. Share knowledge, participate in discussions,
                            and collaborate on projects that make a difference
                            in Colorado and beyond.
                        </p>
                        <div className="d-flex justify-content-center gap-3 flex-wrap">
                            <Button
                                as={Link}
                                to="/communities/guidelines"
                                variant="primary"
                                size="lg"
                            >
                                View Community Guidelines
                            </Button>
                            {user ? (
                                <Button
                                    variant="outline-primary"
                                    size="lg"
                                    onClick={() => setShowCreateModal(true)}
                                >
                                    {isAdmin
                                        ? 'Create a Community'
                                        : 'Request a Community'}
                                </Button>
                            ) : (
                                <Button
                                    as={Link}
                                    to="/login?redirect=/communities"
                                    variant="outline-primary"
                                    size="lg"
                                >
                                    Sign in to request a community
                                </Button>
                            )}
                        </div>
                    </Col>
                </Row>

                {/* Search */}
                <CommunitySearch
                    onSearch={handleSearch}
                    placeholderText={'Search for Communities...'}
                />

                {/* Loading State */}
                {loading && (
                    <div className="d-flex justify-content-center py-5">
                        <Spinner animation="border" variant="primary">
                            <span className="visually-hidden">Loading...</span>
                        </Spinner>
                    </div>
                )}

                {/* Error State */}
                {error && !loading && (
                    <Alert variant="danger" className="text-center">
                        {error}
                    </Alert>
                )}

                {/* Community Cards Grid */}
                {!loading && !error && (
                    <div>
                        <Row className="g-4">
                            {communities.map((community) => (
                                <Col
                                    key={community._id || community.slug}
                                    xs={12}
                                    md={6}
                                    lg={4}
                                >
                                    <CommunityCard
                                        name={community.name}
                                        slug={community.slug}
                                        description={community.description}
                                        memberCount={community.memberCount}
                                        thumbnail={community.icon}
                                    />
                                </Col>
                            ))}
                        </Row>
                    </div>
                )}

                {/* Empty State */}
                {!loading && !error && communities.length === 0 && (
                    <div className="text-center py-5">
                        <p className="text-muted fs-5">
                            No communities available yet. Check back soon!
                        </p>
                    </div>
                )}
            </Container>

            {/* Create Community Modal */}
            <CreateCommunityModal
                show={showCreateModal}
                onHide={() => setShowCreateModal(false)}
                onSubmit={handleCreateCommunity}
            />
        </section>
    );
}
